'use client';

import { Button } from '@koda/koda-components/atoms/Button';
import ModalDialogue from '@koda/koda-components/molecules/ModalDialogue';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { ReactElement, useCallback, useEffect, useRef } from 'react';
import { MainLocale } from '../../../../../../i18n/constants';
import { BUSINESS_INFORMATION_ROUTE, INITIAL_INFORMATION_ROUTE } from '../../../../../../i18n/pathnames/licenses';
import { NavigateNextButton } from '../../../components/NavigationButton';
import { openContactUsMailto, WEB_SUPPORT_EMAIL } from '../../../utils/contactUsMailto';
import { FlowController } from '../../FlowController';
import type { SanityLicense } from '../../types/licenses';
import { LicensePlan } from '../../types/licenses';
import BillingDetails from '../components/BillingDetails';
import { useLicensePlanCalculator } from './hooks/useLicensePlanCalculator';

export type LicensePlanSectionProps = {
  license: SanityLicense;
};

export default function LicensePlanSection({ license }: LicensePlanSectionProps): ReactElement {
  const detailsRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledRef = useRef(false);
  const prevCostRef = useRef<LicensePlan | undefined>(undefined);
  const router = useRouter();
  const locale = useLocale();

  const { licensePlan, businessInformation, calculate, fetchingData, fetchError, clearFetchError, resetLicensePlan } =
    useLicensePlanCalculator();

  const t = useTranslations('licenses.licensePlanSection');

  // Scroll the first time we transition from undefined -> defined plan
  useEffect(() => {
    const appeared = prevCostRef.current === undefined && licensePlan !== undefined;
    if (appeared && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      requestAnimationFrame(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    if (licensePlan === undefined) {
      hasScrolledRef.current = false; // allow future scroll if cost reset
    }
    prevCostRef.current = licensePlan;
  }, [licensePlan]);

  const resetCost = useCallback(() => {
    const flow = new FlowController(license);
    const previousRoute = flow.getAbsoluteRouteInDirection({
      currentStepRoute: BUSINESS_INFORMATION_ROUTE,
      direction: -1,
      locale: locale as MainLocale,
    });

    const hasInitialInformation = previousRoute?.includes(INITIAL_INFORMATION_ROUTE);
    if (hasInitialInformation) {
      router.push(previousRoute);
      return;
    }

    resetLicensePlan();
    // licenseCode is available from props; no need for store field
    // Reset in both local hook and store
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [license, locale, resetLicensePlan, router]);

  const handleContactUs = () => {
    const emailSubject = t('error.emailSubject', { licenseName: license.name?.toLowerCase() ?? '' });
    const emailBody = t('error.emailText', {
      errorCode: fetchError ?? '',
      licenseName: license.name?.toLowerCase() ?? '',
      licenseData: JSON.stringify(businessInformation, null, 2),
    });

    openContactUsMailto({ subject: emailSubject, body: emailBody, to: WEB_SUPPORT_EMAIL });
  };

  return (
    <div className="mt-4">
      <ModalDialogue
        title={t('error.title')}
        description={t('error.description')}
        type="error"
        primaryText={t('error.contactButton')}
        open={!!fetchError}
        onOpenChange={isOpen => {
          if (!isOpen) clearFetchError();
        }}
        onPrimaryAction={handleContactUs}
        isClosable
      />

      {licensePlan ? (
        <div ref={detailsRef} className="mb-4 space-y-6 text-lg font-medium">
          <BillingDetails license={license} />
          <div className="flex flex-col space-y-4">
            <NavigateNextButton license={license} text={t('addToCartButton')} />
            <Button className="w-full" variant="secondaryDark" buttonType="button" onClick={resetCost}>
              {t('editButton')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className="w-full"
          variant="primaryDark"
          buttonType="button"
          onClick={calculate}
          loading={fetchingData}
          testId="license-calculate-button"
        >
          {t('calculateButton')}
        </Button>
      )}
    </div>
  );
}
